import { ProductCtaType, ProductStatus } from '@today/types'
import { z } from 'zod'

// ─── Shared primitives ────────────────────────────────────────────────────────

/**
 * A BCP-47-style locale tag as the catalog accepts it (TO-415): a lower-case
 * 2–3 letter language, then optional `-`-separated 2–8 char subtags. Compared
 * case-sensitively everywhere; at most {@link LOCALE_TAG_MAX} characters.
 */
export const LOCALE_TAG_PATTERN = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/
export const LOCALE_TAG_MAX = 35

/** A locale tag field. */
export const localeTag = z.string().max(LOCALE_TAG_MAX).regex(LOCALE_TAG_PATTERN)

/** `partnerAttributes` is capped by its UTF-8 serialized size, not its key count. */
export const PARTNER_ATTRIBUTES_MAX_BYTES = 16384

const partnerAttributes = z
	.record(z.string(), z.unknown())
	.refine((value) => new TextEncoder().encode(JSON.stringify(value)).length <= PARTNER_ATTRIBUTES_MAX_BYTES, {
		message: `partnerAttributes must serialize to at most ${PARTNER_ATTRIBUTES_MAX_BYTES} bytes`,
	})

/**
 * A CTA target — shape only. The per-type rule (https URL, E.164 phone, action
 * code, the 2048 cap) is product-service's `validateCta`, so it answers its own
 * `PRODUCT_CTA_INVALID` rather than the generic `VALIDATION_ERROR`. The outer
 * cap only bounds the payload.
 */
const ctaTarget = z.string().max(4096).nullable()

// ─── Products (TO-415, product-crud) ──────────────────────────────────────────

/** The v2 fields a writer may set on create and update. */
const productV2Fields = {
	categoryId: z.uuid().nullable(),
	partnerAttributes,
	featured: z.boolean(),
	icon: z.string().min(1).max(64).nullable(),
	imageFileId: z.uuid().nullable(),
	ctaPrimaryType: z.enum(ProductCtaType).nullable(),
	ctaPrimaryTarget: ctaTarget,
	ctaSecondaryType: z.enum(ProductCtaType).nullable(),
	ctaSecondaryTarget: ctaTarget,
}

const productBaseFields = {
	code: z.string().min(1).max(64),
	name: z.string().min(1).max(255),
	defaultLocale: localeTag,
}

/**
 * `POST /products`. STRICT: the server owns `status` (always `DRAFT`),
 * `origin` (always `MANUAL`) and the tenant (from the principal), and
 * `isActive` no longer exists — sending any of them is a 422, never silently
 * dropped.
 */
export const productCreateSchema = z
	.object({ ...productBaseFields, ...z.object(productV2Fields).partial().shape })
	.strict()

/**
 * `PATCH /products/{id}`. A strict partial of the create fields: status moves
 * only through `POST /products/{id}/transitions`, so a `status` key is refused.
 */
export const productUpdateSchema = z
	.object({ ...productBaseFields, ...productV2Fields })
	.partial()
	.strict()

/** `POST /products/{id}/transitions`. */
export const productTransitionSchema = z.object({ targetStatus: z.enum(ProductStatus) }).strict()

// ─── Categories ───────────────────────────────────────────────────────────────

const categoryFields = {
	code: z
		.string()
		.min(1)
		.max(64)
		.regex(/^[a-z0-9][a-z0-9-]*$/),
	name: z.string().min(1).max(255),
	icon: z.string().min(1).max(64).nullable(),
	sortOrder: z.number().int().min(0).max(10000),
}

/** `POST /products/categories`. `icon` defaults to null and `sortOrder` to 0 server-side. */
export const productCategoryCreateSchema = z
	.object({
		code: categoryFields.code,
		name: categoryFields.name,
		icon: categoryFields.icon.optional(),
		sortOrder: categoryFields.sortOrder.optional(),
	})
	.strict()

/** `PATCH /products/categories/{id}`. */
export const productCategoryUpdateSchema = z.object(categoryFields).partial().strict()

// ─── Translations ─────────────────────────────────────────────────────────────

/**
 * `PUT /products/{id}/translations/{locale}` — a full replace. Provenance
 * (`source`, `lastEditedBy`, `lastEditedAt`) is server-set, so the strict
 * object refuses it.
 */
export const productTranslationUpsertSchema = z
	.object({
		name: z.string().min(1).max(255),
		tagline: z.string().max(255).nullable().optional(),
		shortDescription: z.string().max(500).nullable().optional(),
		longDescription: z.string().max(10000).nullable().optional(),
		benefits: z.array(z.string().min(1).max(255)).max(20).optional(),
		mainCtaLabel: z.string().max(80).nullable().optional(),
		secondaryCtaLabel: z.string().max(80).nullable().optional(),
	})
	.strict()

export type ProductCreateInput = z.infer<typeof productCreateSchema>
export type ProductUpdateInput = z.infer<typeof productUpdateSchema>
export type ProductTransitionInput = z.infer<typeof productTransitionSchema>
export type ProductCategoryCreateInput = z.infer<typeof productCategoryCreateSchema>
export type ProductCategoryUpdateInput = z.infer<typeof productCategoryUpdateSchema>
export type ProductTranslationUpsertInput = z.infer<typeof productTranslationUpsertSchema>
